import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Campaign, Collab } from '@foodfluence/shared';
import { needsProductSelection } from './productSelection';

/**
 * Sends a creator to settle their products before an action the server would refuse without one.
 *
 * A brand can add an optional product to a live campaign, which gives every in-flight collab a
 * selection it never made. `advanceCollabToContentSubmission` then rejects with a 400, and the
 * creator has no way to reach the wizard from a Redeem or Mark-shipped button — the flow just
 * fails. Six call sites hit that endpoint; three guarded it, three did not.
 *
 * Returns a function that reports whether it took over. Deliberately not an effect: these are
 * click handlers, and redirecting on render would move a creator who is only looking at the page.
 *
 *     const goToProductSelection = useProductSelectionGuard(collab, campaign);
 *     ...
 *     if (goToProductSelection()) return;
 */
export const useProductSelectionGuard = (
  collab: Collab | undefined | null,
  campaign: Campaign | undefined | null
): (() => boolean) => {
  const navigate = useNavigate();
  const owedSelection = needsProductSelection(collab, campaign);

  return useCallback((): boolean => {
    if (!owedSelection || !collab?.id) return false;
    navigate('/collabs/select-products', {
      state: { collabId: collab.id, destination: owedSelection },
    });
    return true;
  }, [owedSelection, collab?.id, navigate]);
};
